import React from 'react';
import { Link } from 'react-router-dom';

const EPS = 0.0001;

// Tiers ordered best → worst (lower error = better). Each tier carries
// a coordinated palette so the same hue ties the bar zone, the side
// label, the "You're in" badge, and the tier reference inside callouts
// together — Elite emerald, Challenger amber, Runner-Up blue.
const tierOrder = [
  {
    key: 'elite',
    name: 'Elite',
    text: 'text-emerald-700 dark:text-emerald-300',
    bg: 'bg-emerald-100 dark:bg-emerald-900/40',
    chip: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300',
  },
  {
    key: 'challenger',
    name: 'Challenger',
    text: 'text-amber-700 dark:text-amber-300',
    bg: 'bg-amber-100 dark:bg-amber-900/40',
    chip: 'bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300',
  },
  {
    key: 'runner_up',
    name: 'Runner-Up',
    text: 'text-blue-700 dark:text-blue-300',
    bg: 'bg-blue-100 dark:bg-blue-900/40',
    chip: 'bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-300',
  },
];

// "Contender" — qualified but ranked outside the top 11. Engine emits this
// when len(qualified) ≥ 12; the user submitted enough days to count, just
// finished off the leaderboard. Neutral gray, not red — they're still in.
const contenderTier = {
  key: 'contender',
  name: 'Contender',
  text: 'text-gray-600 dark:text-gray-300',
  bg: 'bg-gray-200 dark:bg-gray-700/50',
  chip: 'bg-gray-200 text-gray-700 dark:bg-gray-800 dark:text-gray-200',
};

// "Disqualified" — crossed the MAX_MISSED_DAYS allowance, so they're
// excluded from this month's league assignment entirely. Amber to mirror
// the LeagueIneligibilityBanner rendered above the report.
const disqualifiedTier = {
  key: 'disqualified',
  name: 'Disqualified',
  text: 'text-amber-700 dark:text-amber-300',
  bg: 'bg-amber-100 dark:bg-amber-900/30',
  chip: 'bg-amber-100 text-amber-800 dark:bg-amber-500/20 dark:text-amber-300',
};

// Authoritative mapping from the engine's `league_id` (Layer-3 monthly
// assignment) to the display tier. The badge MUST come from here rather
// than from threshold-vs-score inference — a challenger ranked #6 in a
// pool of 8 qualified peers has a real assignment even though the engine
// can't anchor a Challenger *threshold* without a 10th rank.
const LEAGUE_DISPLAY = {
  elite: tierOrder[0],
  challenger: tierOrder[1],
  runner_up: tierOrder[2],
  unassigned: contenderTier,
  unqualified: disqualifiedTier,
};

// Tier names of the threshold cutoffs, used to build the "X threshold
// hasn't formed yet" footnote when the qualified pool is too small.
const THRESHOLD_REQUIREMENTS = {
  challenger: 10,
  runner_up: 11,
};

// Build the four tier bands of the bar. Always four, equal-height (25% each):
// the bar is a tier *map*, not a numeric axis. Bands whose threshold the
// engine couldn't compute (small qualified pool) render as ghost bands —
// the structure is preserved so the reader sees the full ladder, but missing
// data is honestly flagged rather than hidden.
//
// Each band carries:
//   - top / bottom: vertical % within the bar
//   - threshold: numeric value at this band's *bottom* boundary (the cutoff
//     to enter the next-worse tier). null when undefined. Contender has no
//     lower bound by definition.
//   - defined: whether this band's lower cutoff is known
function buildBands(thresholds) {
  const t = thresholds || {};
  return [
    {
      ...tierOrder[0],
      top: 0,
      bottom: 25,
      threshold: t.elite ?? null,
      defined: t.elite != null,
    },
    {
      ...tierOrder[1],
      top: 25,
      bottom: 50,
      threshold: t.challenger ?? null,
      defined: t.challenger != null,
    },
    {
      ...tierOrder[2],
      top: 50,
      bottom: 75,
      threshold: t.runner_up ?? null,
      defined: t.runner_up != null,
    },
    // Contender has no lower bound — it's "below Runner-Up" with no further
    // cutoff. We mark it defined when the Runner-Up cutoff above it is known,
    // since that gives Contender a real upper edge.
    {
      ...contenderTier,
      top: 75,
      bottom: 100,
      threshold: null,
      defined: t.runner_up != null,
    },
  ];
}

// Vertical % at which to draw the user marker. The marker is pinned to the
// band of the user's *display tier* (leagueId-derived) — leagueId is the
// authoritative tier assignment, so the marker must visually belong to that
// band regardless of the user's numeric score relative to thresholds.
//
// Within the band, the marker is interpolated by score when adjacent
// thresholds anchor a real range (axisFloor for Elite's upper edge, the
// tier-above's threshold for everything else). When the data can't support
// honest interpolation — Contender (no lower bound), missing adjacent
// thresholds — the marker centers in the band.
function computeMarkerPct(tier, bands, userScore, thresholds, axisFloor) {
  if (!tier || userScore == null) return null;
  const band = bands.find((b) => b.key === tier.key);
  if (!band) return null;
  const center = (band.top + band.bottom) / 2;

  let upperVal;
  let lowerVal;
  if (tier.key === 'elite') {
    upperVal = axisFloor != null && axisFloor >= 0 ? axisFloor : null;
    lowerVal = thresholds?.elite;
  } else if (tier.key === 'challenger') {
    upperVal = thresholds?.elite;
    lowerVal = thresholds?.challenger;
  } else if (tier.key === 'runner_up') {
    upperVal = thresholds?.challenger;
    lowerVal = thresholds?.runner_up;
  } else {
    // Contender / Disqualified / fallback: no honest interpolation possible.
    return center;
  }

  if (upperVal == null || lowerVal == null || lowerVal <= upperVal) return center;
  if (userScore <= upperVal) return band.top;
  if (userScore >= lowerVal) return band.bottom;
  const frac = (userScore - upperVal) / (lowerVal - upperVal);
  return band.top + frac * (band.bottom - band.top);
}

// Two adjacent gaps: one to the largest threshold strictly below the user
// (positive diff = user error is *above* it = bad direction) and one to the
// smallest strictly above (negative diff = user error is *below* it = buffer).
function computeAdjacentGaps(userScore, thresholds) {
  if (userScore == null || !thresholds) return [];
  const tiers = tierOrder
    .map((t) => ({ ...t, value: thresholds[t.key] }))
    .filter((t) => t.value != null)
    .sort((a, b) => a.value - b.value);
  if (tiers.length === 0) return [];

  let before = null;
  let after = null;
  for (const t of tiers) {
    if (t.value < userScore - EPS) before = t;
    else if (after == null && t.value > userScore + EPS) after = t;
  }

  const gaps = [];
  if (before) {
    const diff = userScore - before.value;
    gaps.push({
      key: `gap-${before.key}-below`,
      tier: before,
      diff,
      pct: (diff / before.value) * 100,
    });
  }
  if (after) {
    const diff = userScore - after.value;
    gaps.push({
      key: `gap-${after.key}-above`,
      tier: after,
      diff,
      pct: (diff / after.value) * 100,
    });
  }
  return gaps;
}

// Identify the user's current tier from their score (lower error = better).
// Used as a fallback when the caller hasn't passed an authoritative
// `leagueId` from the backend. Prefer `resolveDisplayTier`.
function currentTier(userScore, thresholds) {
  if (userScore == null || !thresholds) return null;
  const { elite, challenger, runner_up: runnerUp } = thresholds;
  if (elite != null && userScore <= elite) return tierOrder[0];
  if (challenger != null && userScore <= challenger) return tierOrder[1];
  if (runnerUp != null && userScore <= runnerUp) return tierOrder[2];
  return null;
}

// Resolve the tier to display. Prefers the backend's `league_id` because
// the engine assigns tiers by rank, not by threshold — in small qualified
// pools (e.g. 7 qualified peers), a rank-6 user is in 'challenger' even
// though no Challenger threshold value exists to anchor the comparison.
function resolveDisplayTier(leagueId, userScore, thresholds) {
  if (leagueId && LEAGUE_DISPLAY[leagueId]) return LEAGUE_DISPLAY[leagueId];
  return currentTier(userScore, thresholds);
}

export const __testables__ = {
  buildBands,
  computeMarkerPct,
  computeAdjacentGaps,
  currentTier,
  resolveDisplayTier,
};

const fmt = (v, digits = 2) => (v == null ? '—' : Number(v).toFixed(digits));
const fmtSigned = (v, digits = 2) => {
  if (v == null) return '—';
  const sign = v >= 0 ? '+' : '−';
  return `${sign}${Math.abs(v).toFixed(digits)}`;
};

/**
 * Vertical league timeline rendered as a four-band tier ladder
 * (Elite / Challenger / Runner-Up / Contender), top-to-bottom from best to
 * worst. Bands have equal height: the bar is a tier *map*, not a numeric
 * axis, so the structure stays legible even when only a subset of tier
 * cutoffs has formed.
 *
 * The displayed tier is sourced from `leagueId` (the engine's monthly
 * assignment) whenever it is provided — this is the only correct answer
 * when the qualified pool is too small to anchor every threshold (e.g. a
 * Challenger ranked #6 of 8 qualified peers still belongs in Challenger,
 * even though there is no 10th-rank score to draw a Challenger cutoff).
 * Without `leagueId`, the component falls back to inferring the tier from
 * score-vs-threshold comparisons.
 *
 * Bands with missing threshold cutoffs render as ghosts (dashed, neutral
 * fill, "TBD" label), so the full ladder is always visible. The user
 * marker is pinned to the band of the resolved display tier and
 * interpolated by score within that band when adjacent thresholds are
 * defined; otherwise it centers in the band. A footnote explains what is
 * missing and why.
 *
 * @param {{
 *   userScore?: number | null,
 *   thresholds?: { elite?: number | null, challenger?: number | null, runner_up?: number | null } | null,
 *   leagueId?: 'elite' | 'challenger' | 'runner_up' | 'unassigned' | 'unqualified' | null,
 *   axisFloor?: number,
 *   metricLabel?: string,
 *   className?: string,
 * }} props
 */
export default function LeagueTimelineCard({
  userScore,
  thresholds,
  leagueId,
  axisFloor = 0,
  metricLabel = 'Score',
  className = '',
}) {
  const gaps = computeAdjacentGaps(userScore, thresholds);
  // Source-of-truth tier: backend `league_id` if available, score-vs-thresholds
  // as a fallback. Critical for small qualified pools where some thresholds
  // are null — see `resolveDisplayTier` docstring.
  const tier = resolveDisplayTier(leagueId, userScore, thresholds);
  const metricLower = (metricLabel || 'score').toLowerCase();

  const promotion = gaps.find((g) => g.diff > 0) || null;
  const buffer = gaps.find((g) => g.diff < 0) || null;

  // Always four equal-height tier bands. Bands with missing thresholds are
  // rendered as ghosts so the full ladder stays visible without inventing
  // proportional geometry the data can't support.
  const bands = buildBands(thresholds);
  const markerPct = computeMarkerPct(tier, bands, userScore, thresholds, axisFloor);

  // Footnote when one or more threshold cutoffs are missing — explains
  // why some bands are rendered as ghosts instead of leaving the user to
  // guess.
  const missingThresholdNote = (() => {
    if (!thresholds) return null;
    const missing = Object.entries(THRESHOLD_REQUIREMENTS)
      .filter(([key]) => thresholds[key] == null)
      .map(([key, min]) => ({
        name: key === 'runner_up' ? 'Runner-Up' : 'Challenger',
        min,
      }));
    if (missing.length === 0) return null;
    const parts = missing.map((m) => `${m.name} (needs ≥${m.min})`);
    return `${parts.join(' · ')} threshold${missing.length > 1 ? 's' : ''} not yet established — too few qualified peers this period.`;
  })();

  return (
    <section className={`h-full flex flex-col ${className}`.trim()}>
      <header className="mb-2 flex items-start justify-between gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] font-medium text-brand-600 dark:text-brand-400">
            League
          </div>
          <h3 className="mt-1.5 text-base font-semibold tracking-tight text-slate-900 dark:text-white">
            How close you are to the next tier
          </h3>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Distance from your current score to the next tier threshold.
          </p>
        </div>
        {userScore != null ? (
          <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wider bg-brand-100 text-brand-700 dark:bg-brand-500/20 dark:text-brand-300 whitespace-nowrap">
            YOU · {fmt(userScore)}
          </span>
        ) : null}
      </header>

      <div className="flex-1 flex flex-col rounded-xl bg-surface dark:bg-gray-870 border border-gray-200/70 dark:border-gray-600/70 shadow-sm p-5 sm:p-6">
        {thresholds ? (
          <div className="flex-1 flex gap-6 sm:gap-8">
            {/* Vertical bar visualization */}
            <div className="flex-none flex" style={{ minHeight: 320 }}>
              {/* Left column: tier names centered in each band */}
              <div className="relative w-20 pr-2 text-right">
                {bands.map((b) => (
                  <div
                    key={`name-${b.key}`}
                    className={`absolute right-2 text-[10px] font-semibold uppercase tracking-wider ${b.defined ? b.text : 'text-gray-400 dark:text-gray-500'}`}
                    style={{
                      top: `${(b.top + b.bottom) / 2}%`,
                      transform: 'translateY(-50%)',
                    }}
                  >
                    {b.name}
                  </div>
                ))}

                {/* Threshold values at band boundaries — only where defined.
                    Each band's `threshold` lives at its BOTTOM edge. */}
                {bands
                  .filter((b) => b.threshold != null)
                  .map((b) => (
                    <div
                      key={`th-${b.key}`}
                      className="absolute right-2 text-[11px] font-semibold font-mono tabular-nums text-gray-700 dark:text-gray-300"
                      style={{ top: `${b.bottom}%`, transform: 'translateY(-50%)' }}
                    >
                      {fmt(b.threshold)}
                    </div>
                  ))}
              </div>

              {/* The bar */}
              <div className="relative w-6 rounded-md overflow-hidden border border-gray-200/70 dark:border-gray-600/70">
                {bands.map((b) => (
                  <div
                    key={`band-${b.key}`}
                    className={`absolute inset-x-0 ${b.defined ? b.bg : 'bg-gray-100 dark:bg-gray-800/40'}`}
                    style={{
                      top: `${b.top}%`,
                      height: `${b.bottom - b.top}%`,
                      // Ghost bands get a dashed separator to distinguish them
                      // from solid (defined) bands without inventing data.
                      borderTop: b.defined
                        ? undefined
                        : '1px dashed rgba(148,163,184,0.6)',
                    }}
                    title={b.defined ? b.name : `${b.name} — threshold not yet established`}
                  />
                ))}
                {/* User marker — a horizontal brand-color bar across the strip */}
                {markerPct != null && userScore != null ? (
                  <div
                    className="absolute -inset-x-1.5 h-0.5 bg-brand-600 dark:bg-brand-400"
                    style={{ top: `${markerPct}%`, transform: 'translateY(-50%)' }}
                  />
                ) : null}
              </div>

              {/* Right of bar: marker arrow + value */}
              <div className="relative w-24 pl-2">
                {markerPct != null && userScore != null ? (
                  <div
                    className="absolute left-1 flex items-center gap-1 text-brand-700 dark:text-brand-300 whitespace-nowrap"
                    style={{ top: `${markerPct}%`, transform: 'translateY(-50%)' }}
                  >
                    <span className="text-base leading-none">◀</span>
                    <span className="text-sm font-bold font-mono tabular-nums leading-none">
                      {fmt(userScore)}
                    </span>
                  </div>
                ) : null}
                {/* "TBD" markers next to undefined-threshold band boundaries —
                    matches the position of the (omitted) numeric label on the
                    left column, so the reader sees explicitly that the cutoff
                    is missing rather than just absent. */}
                {bands
                  .filter((b) => !b.defined && b.key !== 'contender')
                  .map((b) => (
                    <div
                      key={`tbd-${b.key}`}
                      className="absolute left-1 text-[10px] uppercase tracking-wider font-medium text-gray-400 dark:text-gray-500"
                      style={{ top: `${b.bottom}%`, transform: 'translateY(-50%)' }}
                    >
                      TBD
                    </div>
                  ))}
              </div>
            </div>

            {/* Right info column */}
            <div className="flex-1 flex flex-col justify-center gap-5 min-w-0">
              {/* Current tier badge — palette and eyebrow match the tier kind.
                  "You're in" reads naturally for medal tiers; "Currently"
                  fits better for Contender (rank 12+) and Disqualified. */}
              {tier ? (
                <div>
                  <div className="text-[11px] uppercase tracking-[0.12em] font-medium text-gray-500 dark:text-gray-400 mb-1">
                    {tier.key === 'elite' || tier.key === 'challenger' || tier.key === 'runner_up'
                      ? "You're in"
                      : 'Currently'}
                  </div>
                  <span className={`inline-flex items-center rounded-md px-2.5 py-1 text-sm font-bold uppercase tracking-wider ${tier.chip}`}>
                    {tier.name}
                  </span>
                </div>
              ) : (
                <div>
                  <div className="text-[11px] uppercase tracking-[0.12em] font-medium text-gray-500 dark:text-gray-400 mb-1">
                    Currently
                  </div>
                  <span className={`inline-flex items-center rounded-md px-2.5 py-1 text-sm font-bold uppercase tracking-wider ${contenderTier.chip}`}>
                    Out of league
                  </span>
                </div>
              )}

              {/* Promotion callout — tier name carries its own color */}
              {promotion ? (
                <div>
                  <div className="mb-1">
                    <span className="text-[11px] uppercase tracking-wider font-semibold text-red-600 dark:text-red-400">
                      ↓ Reach <span className={promotion.tier.text}>{promotion.tier.name}</span>
                    </span>
                  </div>
                  <div className="flex items-baseline gap-2">
                    <span className="text-xl font-bold font-mono tabular-nums text-red-600 dark:text-red-400">
                      −{fmt(promotion.diff)}
                    </span>
                    <span className="text-xs font-mono tabular-nums text-red-500/80 dark:text-red-400/80">
                      ({fmtSigned(-promotion.pct, 1)}%)
                    </span>
                    <span className="text-xs text-gray-500 dark:text-gray-400">to drop</span>
                  </div>
                </div>
              ) : null}

              {/* Buffer callout — tier name carries its own color */}
              {buffer ? (
                <div>
                  <div className="mb-1">
                    <span className="text-[11px] uppercase tracking-wider font-semibold text-emerald-600 dark:text-emerald-400">
                      ↑ Buffer to <span className={buffer.tier.text}>{buffer.tier.name}</span>
                    </span>
                  </div>
                  <div className="flex items-baseline gap-2">
                    <span className="text-xl font-bold font-mono tabular-nums text-emerald-600 dark:text-emerald-400">
                      {fmt(Math.abs(buffer.diff))}
                    </span>
                    <span className="text-xs font-mono tabular-nums text-emerald-500/80 dark:text-emerald-400/80">
                      ({fmtSigned(buffer.pct, 1)}%)
                    </span>
                    <span className="text-xs text-gray-500 dark:text-gray-400">cushion</span>
                  </div>
                </div>
              ) : null}

              {!promotion && !buffer ? (
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  No adjacent thresholds to compare against yet.
                </p>
              ) : null}
            </div>
          </div>
        ) : (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            League thresholds aren&apos;t available for this period.
          </p>
        )}

        <div className="mt-auto pt-4 space-y-1">
          {missingThresholdNote ? (
            <p className="text-[11px] italic text-gray-500 dark:text-gray-400">
              {missingThresholdNote}{' '}
              <Link
                to="/faq"
                className="not-italic font-medium text-brand-600 hover:text-brand-700 dark:text-brand-400 dark:hover:text-brand-300 underline-offset-2 hover:underline"
              >
                How does this affect payments?
              </Link>
            </p>
          ) : null}
          <p className="text-[11px] text-gray-500 dark:text-gray-400">
            Lower {metricLower} is better.
          </p>
        </div>
      </div>
    </section>
  );
}
